import { Container } from 'pixi.js';
import { ScreenEvents } from "./ScreenEvents";
import { UiContainer } from "../ui/UiContainer";
import { PixiApp } from "../app/PixiApp";
import { Timeline } from "../animation/Timeline";
import { TimelineOptions } from "../animation/TimelineOptions";
import { TweenOptions } from "../animation/TweenOptions";
import { Tween } from "../animation/Tween";
import { ScreenPin } from "./ScreenPin";
import { DomElementInteractionEventTypes } from "@cc/html5-dom-layer";
import { SoundManager } from "@cc/soundjs-manager";


/**
 * Game screen base class.
 */
export class Screen extends Container {
  /**
   * PixiApp instance reference.
   */
  public app: PixiApp;

  /**
   * UI container layer.
   */
  public ui: UiContainer;

  /**
   */
  public screenState?: any = {};

  /**
   * @param app PixiApp instance reference.
   */
  constructor(app: PixiApp) {
    super();
    this.app = app;
    this.ui = new UiContainer();
  }

  /**
   * Called on instancing the screen.
   */
  public init() {}
  public callInit(screenState?: any) {
    this.screenState = screenState;
    this.emit(ScreenEvents.Init);
    this.init();
  }

  /**
   * Called on showing the screen.
   */
  public start() {}
  public callStart() {
    this.emit(ScreenEvents.Start);
    this.start();
  }

  /**
   * Update called while screen is shown.
   *
   * @param width
   * @param height
   */
  public resize(width: number, height: number) {}
  public callResize(width: number, height: number) {
    this.ui.resize(width, height);
    this.pinChildren(this.ui);
    this.emit(ScreenEvents.Resize, width, height);
    this.resize(width, height);
  }

  /**
   * Called each frame while screen is shown.
   */
  public update(dt: number) {}
  public callUpdate(dt: number) {
    this.emit(ScreenEvents.Update, dt);
    this.update(dt);
  }

  /**
   * Called each frame while screen is shown after main update.
   */
  public postUpdate(dt: number) {}
  public callPostUpdate(dt: number) {
    this.emit(ScreenEvents.Update, dt);
    this.postUpdate(dt);
  }

  /**
   * Called on hiding the screen.
   */
  public hide() {}
  public callHide() {
    this.visible = false;
    this.emit(ScreenEvents.Hide);
    this.hide();
  }

  /**
   * Called on app pause.
   */
  public pause() {}
  public callPause() {
    this.emit(ScreenEvents.Pause);
  }

  /**
   * Transition animation called upon showing this screen.
   */
  public transitionIn() {}
  public callTransitionIn() {
    this.emit(ScreenEvents.TransitionInStart);
    this.transitionIn();
  }

  /**
   * Transition animation called upon showing this screen.
   */
  public transitionInEnd() {}
  public callTransitionInEnd() {
    this.emit(ScreenEvents.TransitionInEnd);
    this.transitionInEnd();
  }

  /**
   * Transition animation called upon hiding or destroying this screen.
   */
  public transitionOut() {}
  public callTransitionOut() {
    this.emit(ScreenEvents.TransitionOutStart);
    this.transitionOut();
  }

  /**
   * Transition animation called upon hiding or destroying this screen.
   */
  public transitionOutEnd() {}
  public callTransitionOutEnd() {
    this.emit(ScreenEvents.TransitionOutEnd);
    this.transitionOutEnd();
  }

  /**
   * Set screen interactions enabled/disabled.
   */
  public setInteractionEnabled(enabled: boolean) {
    this.interactiveChildren = enabled;
    this.ui.interactiveChildren = enabled;
  }

  /**
   */
  public restart() {
    this.app.screens.showScreen(this.name);
  }

  /**
   * Called on disposing the screen.
   */
  public destroy(options?: PIXI.DestroyOptions) {
    this.emit(ScreenEvents.Destroy);
    // Timeout.clearAll(this.name);
    Tween.removeAllTweens();
    if (this.app.domLayer) this.removeDomlayerEventListeners();
    this.ui.destroy();
    super.destroy({ ...{ children: true }, ...options });
  }

  /**
   *
   * @param container
   */
  private pinChildren(container: PIXI.Container) {
    for (const child of container.children) {
      if (child instanceof PIXI.Container) {
        const sprite = child as PIXI.Sprite;
        if (sprite.texture && sprite.anchor) {
          if (child.screenPin) this.pinSprite(sprite);
        }
      }
    }
  }

  /**
   * Create a timeout.
   *
   * @param options
   */
  // public timeout(options: TimeoutOptions): Timeout {
  //   return this.app.timeout({
  //     ...{ group: this.name },
  //     ...options
  //   });
  // }

  /**
   * Create a tween.
   *
   * @param options
   */
  public tween(target: any, options?: TweenOptions): Tween {
    return Tween.get(target, {
      ...{ group: this.name },
      ...options
    });
  }

  /**
   * Create a tween.
   *
   * @param options
   */
  public wait(ms: number): Tween {
    return Tween.get(this, {
      ...{ group: this.name }
    }).wait(ms) as Tween;
  }

  /**
   * Create a tween.
   *
   * @param options
   */
  public timeline(options?: TimelineOptions): Timeline {
    return new Timeline({
      ...{ group: this.name },
      ...options
    });
  }

  /**
   *
   * @param enabled
   */
  public logScreenPositionOnClick(enabled: boolean) {
    if (!!enabled) {
      this.on("pointerdown", this.logScreenPosition, this);
      this.interactive = true;
    } else {
      this.off("pointerdown", this.logScreenPosition, this);
      this.interactive = false;
    }
  }

  public get sound(): SoundManager {
    return this.app.sound;
  }

  /**
   *
   * @param e
   */
  private logScreenPosition(e: PIXI.interaction.InteractionEvent) {
    console.log(e.data.getLocalPosition(this));
  }

  /**
   *
   * @param sprite
   * @param x
   * @param y
   */
  public pinSprite(sprite: PIXI.Sprite) {
    const w = this.app.resizer.width;
    const h = this.app.resizer.height;
    const oX = w * this.app.screenManager.screenAnchor.x;
    const oY = h * this.app.screenManager.screenAnchor.y;
    const pad = sprite.screenPadding || 0;
    switch (sprite.screenPin) {
      case ScreenPin.TopMiddle:
      case ScreenPin.BottomMiddle:
      case ScreenPin.Middle:
        sprite.x = 0;
        break;
      case ScreenPin.BottomLeft:
      case ScreenPin.MiddleLeft:
      case ScreenPin.TopLeft:
        sprite.x = -oX + sprite.texture.width * sprite.anchor.x + pad;
        break;
      case ScreenPin.BottomRight:
      case ScreenPin.MiddleRight:
      case ScreenPin.TopRight:
        sprite.x = w - oX - sprite.texture.width * (-sprite.anchor.x + 1) - pad;
        break;
      default:
        break;
    }

    switch (sprite.screenPin) {
      case ScreenPin.MiddleLeft:
      case ScreenPin.MiddleRight:
      case ScreenPin.Middle:
        sprite.y = 0;
        break;
      case ScreenPin.TopLeft:
      case ScreenPin.TopRight:
      case ScreenPin.TopMiddle:
        sprite.y = -oY + sprite.texture.height * sprite.anchor.y + pad;
        break;
      case ScreenPin.BottomLeft:
      case ScreenPin.BottomRight:
      case ScreenPin.BottomMiddle:
        sprite.y = h - oY - sprite.texture.height * (-sprite.anchor.y + 1) - pad;
        break;
      default:
        break;
    }
  }

  public removeDomlayerEventListeners() {
    for (const key in DomElementInteractionEventTypes) {
      if (DomElementInteractionEventTypes.hasOwnProperty(key)) {
        const value = DomElementInteractionEventTypes[key];
        this.app.domLayer.off(value);
      }
    }
  }

  public addDomLayerEventListeners() {
    if (this.app.domLayer) {
      this.app.domLayer.on(
        DomElementInteractionEventTypes.MouseOver,
        this.onDomElementMouseOverEvent,
        this
      );
      this.app.domLayer.on(
        DomElementInteractionEventTypes.MouseDown,
        this.onDomElementMouseDownEvent,
        this
      );
      this.app.domLayer.on(
        DomElementInteractionEventTypes.MouseUp,
        this.onDomElementMouseUpEvent,
        this
      );
      this.app.domLayer.on(
        DomElementInteractionEventTypes.Click,
        this.onDomElementClickEvent,
        this
      );
      this.app.domLayer.on(
        DomElementInteractionEventTypes.TouchStart,
        this.onDomElementTouchStartEvent,
        this
      );
      this.app.domLayer.on(
        DomElementInteractionEventTypes.TouchEnd,
        this.onDomElementTouchEndEvent,
        this
      );
      this.app.domLayer.on(
        DomElementInteractionEventTypes.PointerTap,
        this.onDomElementPointerTapEvent,
        this
      );
      this.app.domLayer.on(
        DomElementInteractionEventTypes.PointerAudio,
        this.onDomElementPointerAudioEvent,
        this
      );
    }
  }

  onDomElementMouseOverEvent(event: DomElementInteractionEventTypes) {}
  onDomElementMouseDownEvent(event: DomElementInteractionEventTypes) {}
  onDomElementMouseUpEvent(event: DomElementInteractionEventTypes) {}
  onDomElementClickEvent(event: DomElementInteractionEventTypes) {}
  onDomElementTouchStartEvent(event: DomElementInteractionEventTypes) {}
  onDomElementTouchEndEvent(event: DomElementInteractionEventTypes) {}
  onDomElementPointerTapEvent(event: DomElementInteractionEventTypes) {}
  onDomElementPointerAudioEvent(event: DomElementInteractionEventTypes) {}
}
